import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

import {
  CheckCircle2,
  Circle,
  Clock,
  DollarSign,
  ArrowRight,
  Upload,
  X,
  Image as ImageIcon,
  ChevronDown,
  ChevronUp,
  History,
  Check,
  XCircle,
  AlertCircle,
  Delete,
  Trash,
  Trash2,
  Pencil,
  Send,
  CheckCircle,
  PlayCircle,
  Wrench,
  Flag,
  ThumbsUp,
  ThumbsDown,
  FileText,
  Edit,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserDetails, setSpinnerLoading } from 'slices/userProfile/reducer';
import { getDecodeId } from 'slices/userProfile/thunk';
import axios from 'axios';
import apiUrl from 'slices/apiEndpoint';
import { getAuthHeader } from 'helpers/jwt-token-access/auth-token-header';
import { Dialog, DialogContent } from '@mui/material';
import { DialogFooter } from './ui/dialog';
import { CallAPIPostPromise } from 'Common/commaon';
import api from 'Common/api';

interface Activity {
  id: string;
  timestamp: string;
  user: string;
  role: 'contractor' | 'homeowner';
  action: string;
  details?: string;
}

interface Milestone {
  id: string;
  name: string;
  percentage: number;
  amount: number;
  status: 'pending' | 'in-progress' | 'completed' | 'paid';
  description?: string;
  workStage: 'not-started' | 'started' | 'rough' | 'completed';
  inspectionImages: string[];
  inspectionStatus: 'pending' | 'approved' | 'disapproved';
  inspectionDisapprovalReason?: string;
  activities: Activity[];
  dueDate?: string;
}

interface ExpandableMilestoneCardProps {
  milestone: any;
  index: number;
  isLast: boolean;
  onUpdate: (milestone: Milestone) => void;
  onPayClick: (id: string) => void;
  project:any;
  setMilestones:any;
  onEditMilestone:any;
  milestones:any;
  setRefresh:any;
  isEscrowProject:any;
  setLoading:any;
  isEditDisable:any
}

export function ExpandableMilestoneCard({
  milestone,
  index,
  isLast, setLoading,
  onUpdate,setRefresh,isEscrowProject,
  onPayClick,project,setMilestones,onEditMilestone,milestones,isEditDisable
}: ExpandableMilestoneCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [pendingImages, setPendingImages] = useState<any[]>([]);
  const [showDisapprovalDialog, setShowDisapprovalDialog] = useState(false);
  const [disapprovalReason, setDisapprovalReason] = useState('');
  const user = useSelector(selectUserDetails)
  const [selectedImage,setSelectedImage]=useState(null)
  const [currentStage,setCurrentStage] =useState("started")
  const currentRole = user[0]?.user_type !== "inspector"
    ? Number(project?.p_created_by) === Number(getDecodeId())
      ? "contractor"
      : "homeowner"
    : "inspector"
  const [actionRequired,setIsActionRequired]= useState('')
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
const [approvalNotes, setApprovalNotes] = useState('');

const [enlargedImage, setEnlargedImage] = useState<string>("");

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle2 className="h-4 w-4 text-white" />;
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-white" />;
      case 'in-progress':
        return <div className="h-2 w-2 rounded-full bg-white animate-pulse" />;
      default:
        return <div className="h-2 w-2 rounded-full bg-white opacity-40" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return '#1f57d6';
      case 'completed':
        return '#16a34a';
      case 'in-progress':
      case 'started':
      case 'rough':
        return '#d97706';
      case 'pending':
      case 'not-started':
        return '#64748b';
      default:
        return '#64748b';
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'paid':
        return { bg: 'var(--app-surface)', border: '1px solid var(--app-border)' };
      case 'completed':
        return { bg: 'var(--app-surface)', border: '1px solid var(--app-success-text)' };
      case 'in-progress':
        return { bg: 'var(--app-surface)', border: '1px solid var(--app-warning-text)' };
      default:
        return { bg: 'var(--app-surface)', border: '1px solid var(--app-border)' };
    }
  };



  const removeImage = (imageIdentifier: string | File, isPending: boolean = false) => {
    if (currentRole !== 'contractor') return;

    if (isPending) {
      // Remove from pending files
      setPendingImages(prev =>
        prev.filter(file => file.name !== (typeof imageIdentifier === 'string' ? imageIdentifier : imageIdentifier.name))
      );
      toast.success('Image removed from queue');
    } else {
      // Remove from already uploaded inspection images (URLs)
      const newImages = milestone?.pm_inspection_details[currentStage]?.inspectionUrl.filter(
        (img: string) => img !== imageIdentifier
      );



      onUpdate({
        ...milestone,
        inspectionImages: newImages,
        // activities: [...milestone.activities, activity],
      });

      toast.success('Image removed');
    }
  };


  const handleWorkStageChange = (stage: Milestone['workStage']) => {
    if (currentRole === 'contractor') {
      // Define stage order
      const stageOrder = ['not-started', 'started', 'rough', 'completed'];
      const currentIndex = stageOrder.indexOf(milestone.workStage);
      const newIndex = stageOrder.indexOf(stage);

      // Prevent going backward
      if (newIndex < currentIndex) {
        toast.error('Cannot move to a previous stage. Stages can only progress forward.');
        return;
      }


      let newStatus = milestone.status;
      if (stage === 'completed' && milestone.status !== 'paid') {
        newStatus = 'completed';
      } else if (stage === 'started' || stage === 'rough') {
        newStatus = 'in-progress';
      }
      setCurrentStage(stage)

      onUpdate({
        ...milestone,
        workStage: stage,
        status: newStatus,
        // activities: [...milestone.activities, activity]
      });
      // toast.success(`Work stage updated to ${stage?.replace('-', ' ')}`);
    }
  };


  const addNote = () => {
    if (newNote.trim() && currentRole) {


      onUpdate({
        ...milestone,
        // activities: [...milestone.activities, activity]
      });
      setNewNote('');
      toast.success('Note added');
    }
  };

  const handleInspectionApproval = () => {
    if (currentRole === 'homeowner'||currentRole === 'inspector') {
      setLoading(true)

      CallAPIPostPromise(`/contract/update-inspections-status`,{
        "milestoneId": milestone.id,
        "stage": currentStage,
        "isApproved": true,
        "userId": getDecodeId(),
        "approveMsg":approvalNotes,
        nextMilesoneId:!isLast && currentStage==="completed" ?milestones[index+1].id:""
      }).then((res:any)=>{
        setRefresh((r:any)=>!r)
      toast.success('Inspection images approved!');

      }).catch((err:any)=>{
        console.log(err)
        setLoading(false)

      })
      setShowApprovalDialog(false);


    }
  };
  const getActivityIcon = (action: string) => {
    const actionLower = action.toLowerCase();

    // Work stage activities - more specific matching
    if (actionLower.includes('milestone created') || actionLower.includes('started work')) {
      return { Icon: Edit, color: '#6b7280', bg: '#f3f4f6'  };
    }
    if (actionLower.includes('rough stage') || actionLower.includes('rough work')) {
      return { Icon: Wrench, color: '#f59e0b', bg: '#fef3c7' };
    }
    if (actionLower.includes('to completed') || (actionLower.includes('work stage') && actionLower.includes('completed'))) {
      return { Icon: Flag, color: '#10b981', bg: '#d1fae5' };
    }

    // Inspection activities
    if (actionLower.includes('submitted') && actionLower.includes('image')) {
      return { Icon: Send, color: '#8b5cf6', bg: '#ede9fe' };
    }
    if (actionLower.includes('upload') && actionLower.includes('image')) {
      return { Icon: Upload, color: '#8b5cf6', bg: '#ede9fe' };
    }
    if (actionLower.includes('approved inspection')) {
      return { Icon: ThumbsUp, color: '#10b981', bg: '#d1fae5' };
    }
    if (actionLower.includes('disapproved inspection') ||actionLower.includes('reject')) {
      return { Icon: ThumbsDown, color: '#ef4444', bg: '#fee2e2' };
    }
    if ((actionLower.includes('cancelled') || actionLower.includes('removed')) && actionLower.includes('image')) {
      return { Icon: X, color: '#ef4444', bg: '#fee2e2' };
    }

    // Milestone activities
    if (actionLower.includes('approved')) {
      return { Icon: CheckCircle2, color: '#10b981', bg: '#d1fae5' };
    }
    if (actionLower.includes('payment')) {
      return { Icon: DollarSign, color: '#059669', bg: '#d1fae5' };
    }
    if (actionLower.includes('created milestone')) {
      return { Icon: Circle, color: '#6b7280', bg: '#f3f4f6' };
    }
    if (actionLower.includes('added note')) {
      return { Icon: FileText, color: '#6b7280', bg: '#f3f4f6' };
    }

    // Default
    return { Icon: Edit, color: '#6b7280', bg: '#f3f4f6' };
  };

  const handleInspectionDisapproval = () => {
    if ((currentRole === 'homeowner' && disapprovalReason.trim()) ||(currentRole === 'inspector' && disapprovalReason.trim())) {
      setLoading(true)
      CallAPIPostPromise(`/contract/update-inspections-status`,{
        "milestoneId": milestone.id,
        "stage": currentStage,
        "isApproved": false,
        "userId": getDecodeId(),
        rejectedReason:disapprovalReason,
      }).then((res:any)=>{
        setRefresh((r:any)=>!r)
        toast.error('Inspection images disapproved');

      }).catch((err:any)=>{
        console.log(err)
        setLoading(false)
      })


      setShowDisapprovalDialog(false);
      setDisapprovalReason('');
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && currentRole === 'contractor') {
      // 💫 Convert FileList → Array<File>
      const selectedFiles = Array.from(files);

      // 💫 Merge new files with existing pending ones
      setPendingImages(prev => [...prev, ...selectedFiles]);


      // 🧹 Reset file input
      event.target.value = '';
    }
  };

  const handleSubmitImages = async () => {
    if (currentRole !== 'contractor' ||  (pendingImages.length === 0) && Number(project?.p_escrow)===1) return;

    if(!currentStage){
      toast.warning("Select stage first")
      return
    }


  //test
    const stage = currentStage  // 👈 whatever stage is active (start, rough, completed)
    try {
      const headers = getAuthHeader();
      // Step 1️⃣ Send images to backend API
      const formData = new FormData();
      pendingImages.forEach(file => formData.append('files', file));
      formData.append('milestoneId', milestone.id);
      formData.append('stage', stage);
      formData.append('userId', getDecodeId());
      formData.append('nextMilesoneId', !isLast && currentStage==="completed" ?milestones[index+1].id:"");

      setLoading(true)
   const head = { 'Content-Type': 'multipart/form-data', ...headers}

    await api.post(`/contract/upload-inspections`, formData, {
        headers:head ,
      }) as any




      setRefresh((r:any)=>!r)
      setPendingImages([]);
      if(Number(project?.p_escrow) ===1){
        toast.success(`${pendingImages.length} image(s) submitted for homeowner review`);

      }else{
        toast.success(`Stage updated`);

      }
    } catch (err) {
      console.error('❌ Error submitting inspection images:', err);
      setLoading(false)

      toast.error('Failed to submit inspection images. Please try again.');
    }
  };
  const rejectedReason =milestone?.pm_inspection_details?milestone?.pm_inspection_details[currentStage]?.rejectedReason:""
  const Insp_rejectedReason =milestone?.pm_inspection_details?milestone?.pm_inspection_details[currentStage]?.inspectionRejectedReason:""
useEffect(() => {
  if (project?.p_status !== "LIVE") return;

  setCurrentStage(milestone.workStage === "not-started" ? "started" : milestone.workStage);
  const actionRequired = rejectedReason
    ? `Your images were disapproved by the homeowner. Reason: ${rejectedReason}`
    : Insp_rejectedReason
      ? `Your images were disapproved by the inspector. Reason: ${Insp_rejectedReason}`
      : "";
  setIsActionRequired(actionRequired);
  // LIVE projects open only work that needs attention. Pending milestones stay
  // compact so the timeline remains scannable.
  setIsExpanded(milestone.status === "in-progress" || Boolean(actionRequired));
}, [project?.p_status, milestone.id, milestone.status, milestone.workStage, rejectedReason, Insp_rejectedReason]);

const isHomeOwnerApproved =milestone?.pm_inspection_details? milestone?.pm_inspection_details[currentStage]?.isApproved:false

  return (
    <div className="relative">
      {/* Timeline connector */}
      {!isLast && (
        <div className="milestone-card__connector absolute left-3.5 top-14 w-0.5 md:left-3.5"
             style={{ height: 'calc(100% - 2rem)' }} />
      )}

      {/* Milestone card */}
      <div className="relative flex gap-3 md:gap-4 pb-4">
        {/* Timeline icon */}
        <div className="relative z-10 flex-shrink-0 mt-3">
          <div className="milestone-card__timeline-icon rounded-full shadow-sm flex items-center justify-center"
               style={{
                 width: '28px',
                 height: '28px',
                 backgroundColor: getStatusColor(milestone.status)
               }}>
            {getStatusIcon(milestone.status)}
          </div>
        </div>

        {/* Content card */}
        <Card
          className="milestone-card flex-1 border hover:shadow-md transition-all duration-200"
          style={{
            backgroundColor: getStatusBg(milestone.status).bg,
            border: getStatusBg(milestone.status).border
          }}
        >
          {/* Header - Always visible */}

          <div
            className={`milestone-card__header p-4 ${project?.p_status === "LIVE" ? "cursor-pointer" : ""}`}
            role={project?.p_status === "LIVE" ? "button" : undefined}
            tabIndex={project?.p_status === "LIVE" ? 0 : undefined}
            aria-expanded={project?.p_status === "LIVE" ? isExpanded : undefined}
            aria-label={project?.p_status === "LIVE" ? `${isExpanded ? "Collapse" : "Expand"} ${milestone.name} milestone` : undefined}

            onClick={() => {
              if(project?.p_status ==="LIVE"){
                setIsExpanded(!isExpanded)
              }
            }}
            onKeyDown={(event) => {
              if (project?.p_status === "LIVE" && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                setIsExpanded(!isExpanded);
              }
            }}
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="text-xs" style={{ color: '#9ca3af' }}>Milestone {index + 1}</span>
                  <ArrowRight className="h-3 w-3 hidden sm:block" style={{ color: '#d1d5db' }} />
                  <h3 className="text-lg">{milestone.name}</h3>
                </div>

                {milestone.description && !isExpanded && (
                  <p className="text-xs mb-2 line-clamp-2" style={{ color: '#6b7280' }}>
                    {milestone.description}
                  </p>
                )}

                <div className="flex items-center gap-2.5 flex-wrap">
                  <Badge
                    variant="outline"
                    className="border-current bg-white text-xs"
                    style={{ color: 'var(--app-text-muted)', borderColor: 'var(--app-border)' }}
                  >
                    {milestone.percentage}%
                  </Badge>
                  <div className="flex items-center gap-0.5 text-sm" style={{ color: 'var(--app-text)' }}>
                    <DollarSign className="h-3.5 w-3.5" />
                    <span>{Number(milestone?.amount)?.toLocaleString()}</span>
                  </div>
                  <Badge
                    className="text-xs bg-red-100"
                    style={{
                      backgroundColor: milestone.workStage === 'completed' ? '#1f57d6' :
                        milestone.workStage === 'rough' ? '#6b7280' :
                        milestone.workStage === 'started' ? '#9ca3af' :
                        '#d1d5db',
                      color: milestone.workStage === 'not-started' ? '#6b7280' : 'white'
                    }}
                  >
                    {milestone?.workStage?.replace('-', ' ')}
                  </Badge>
                </div>
              </div>

              {/* Status and action */}
              <div className="flex flex-col items-start sm:items-end gap-2">
                {project?.p_status ==="LIVE" &&     <Badge
                  className="border-0 text-white text-xs"
                  style={{ backgroundColor: getStatusColor(milestone.status) }}
                >
                  {milestone.status === 'paid' && <CheckCircle2 className="mr-1 h-3 w-3" />}
                  {milestone.status === 'in-progress' && <Clock className="mr-1 h-3 w-3" />}
                  {milestone.status === 'paid' ? 'Paid' :
                   milestone.status === 'completed' ? 'Completed' :
                   milestone.status}
                </Badge>
}

                {/* {milestone.status === 'completed' && currentRole === 'homeowner' && (
                  <Button
                    size="sm"
                    className="shadow-sm text-xs h-7 "
                    style={{ backgroundColor: '#1f57d6', color: 'white' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPayClick(milestone.id);
                    }}
                  >
                    <DollarSign className="mr-1 h-3 w-3" />
                    Pay Now
                  </Button>
                )} */}

{project?.p_status==="LIVE" ?    <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7"
                  style={{ color: 'var(--app-text-muted)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(!isExpanded);
                  }}
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="mr-1 h-3 w-3" />
                      Collapse
                    </>
                  ) : (
                    <>
                      <ChevronDown className="mr-1 h-3 w-3" />
                      Expand
                    </>
                  )}
                </Button> :
                <div className='flex'>
                    <Button
                  variant={'ghost'}
                  size="sm"

                  className="text-xs  bg-gray-200 px-3 mr-2"
                  style={{ color: '#6b7280' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditMilestone(milestone)

                  }}
                >

                    <>
                      <Pencil className=" text-blue-500 h-3 " />

                    </>

                </Button>
{!isEscrowProject &&    <Button
                  variant={'ghost'}
                  size="sm"
                  className="text-xs  bg-gray-200 px-3"
                  style={{ color: '#6b7280' }}
                  disabled={isEditDisable}
                  onClick={(e) => {
                    if(isEditDisable){
                      return
                    }
                    e.stopPropagation();
              setMilestones((m:any)=>{
                return m.filter((ml:any)=>ml.id !==milestone.id)
              })
                  }}
                >

                    <>
                      <Trash2 className=" text-red-500 h-3 " />

                    </>

                </Button>}


                </div>



                }

              </div>
            </div>
          </div>

          {/* Expanded content */}
          {isExpanded && (
            <div className="px-4 pb-4 space-y-4 border-t pt-4" style={{ borderColor: '#e5e7eb' }}>
              {/* Description */}
              {milestone.description && (
                <div className="bg-white rounded-lg p-3 border" style={{ borderColor: '#e5e7eb' }}>
                  <Label className="text-xs mb-1.5 block" style={{ color: '#6b7280' }}>Description</Label>
                  <p className="text-sm leading-relaxed" style={{ color: '#374151' }}>{milestone.description}</p>
                </div>
              )}

              {/* Contractor Controls */}
              {currentRole === 'contractor' && (
                <div className="bg-white rounded-lg p-4 border" style={{ borderColor: '#e5e7eb' }}>
                  <h4 className="mb-3 flex items-center gap-2 text-sm" style={{ color: '#18202a' }}>
                    <div className="p-1.5 rounded" style={{ backgroundColor: '#eff6ff' }}>
                      <ImageIcon className="h-3.5 w-3.5" style={{ color: '#1f57d6' }} />
                    </div>
                    Contractor Controls
                  </h4>


                  {milestone?.pm_inspection_details[currentStage]?.inspectionUrl.length ===0 &&    <div className="mb-4">
                    <Label className="mb-2 block text-xs" style={{ color: '#6b7280' }}>Update Work Stage</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {([ 'started', 'rough', 'completed'] as const).map((stage) => {
                        const stageOrder = [ 'started', 'rough', 'completed'];

                        const isDisabled = milestone?.pm_inspection_details[stage].isApproved ||milestone?.pm_inspection_details[currentStage]?.inspectionUrl.length > 0

                        return (
                          <Button
                            key={stage}
                            variant={currentStage === stage ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handleWorkStageChange(stage)}
                            disabled={isDisabled}
                            style={currentStage === stage ? { backgroundColor: '#1f57d6', color: 'white' } : {}}
                            className={`text-xs   h-9 ${isDisabled ? 'opacity-50 cursor-not-allowed ' : ''}`}
                          >
                            {stage === currentStage && <Check className="mr-1.5 h-3 w-3" />}
                            {stage?.replace('-', ' ')}
                          </Button>
                        );
                      })}
                    </div>

                  </div>}


                  {/* Image Upload */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs" style={{ color: '#6b7280' }}>Inspection Images</Label>

                      {/* Inspection Status Badge for Contractor */}

                    </div>

                    {/* Disapproval Notice for Contractor */}
                    { rejectedReason && (
                      <div className="mb-3 p-3 rounded-lg border-2" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca' }}>
                        <div className="flex gap-2">
                          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }} />
                          <div className="flex-1">
                            <p className="text-xs mb-1" style={{ color: '#991b1b' }}>Homeowner Feedback:</p>
                            <p className="text-xs leading-relaxed" style={{ color: '#7f1d1d' }}>{milestone?.pm_inspection_details[currentStage]?.rejectedReason }</p>
                            <p className="text-xs mt-2" style={{ color: '#991b1b' }}>
                              Please upload revised images addressing the issues above.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                                    { Insp_rejectedReason && (
                      <div className="mb-3 p-3 rounded-lg border-2" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca' }}>
                        <div className="flex gap-2">
                          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }} />
                          <div className="flex-1">
                            <p className="text-xs mb-1" style={{ color: '#991b1b' }}>Inspector Feedback:</p>
                            <p className="text-xs leading-relaxed" style={{ color: '#7f1d1d' }}>{Insp_rejectedReason  }</p>
                            <p className="text-xs mt-2" style={{ color: '#991b1b' }}>
                              Please upload revised images addressing the issues above.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {Number(project?.p_escrow) ===1 &&  <div className="border-2 border-dashed rounded-lg p-4 text-center transition-colors hover:border-blue-300" style={{ borderColor: '#d1d5db' }}>
                      <input
                        type="file"
                        id={`images-${milestone.id}`}
                        className="hidden"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                      <label htmlFor={`images-${milestone.id}`} className="cursor-pointer block">
                        <div className="p-2 rounded-lg inline-block mb-2" style={{ backgroundColor: '#eff6ff' }}>
                          <Upload className="h-5 w-5" style={{ color: '#1f57d6' }} />
                        </div>
                        <p className="text-xs" style={{ color: '#6b7280' }}>
                          Click to upload inspection images
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>
                          PNG, JPG up to 10MB
                        </p>
                      </label>
                    </div>}

                    {/* Submitted Images */}
                    {milestone?.pm_inspection_details[currentStage]?.inspectionUrl.length > 0 && (
                      <div>
                         <div className='flex justify-between mt-3 items-center'>


<div className=" space-y-2">
                        <div className="text-xs" style={{ color: '#6b7280' }}>
                          Submitted files ({milestone.pm_inspection_details[currentStage].inspectionUrl.length})  - {currentStage}
                        </div>

                      </div>


                            <Badge className="text-xs border-0 bg-orange-600" style={{ backgroundColor: '#f59e0b', color: 'white' }}>
                              <Clock className="h-3 w-3 mr-1" />
                              Awaiting Review
                            </Badge>
                      </div>
                          {milestone.pm_inspection_details[currentStage].inspectionUrl.map((img:any, idx:any) => (
                          <div key={idx} className="flex items-center justify-between p-2.5 mt-2 rounded-lg border hover:shadow-sm transition-shadow" style={{ borderColor: '#e5e7eb', backgroundColor: '#fafafa' }}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <div className="p-1.5 rounded" style={{ backgroundColor: '#f3f4f6' }}>
                                <ImageIcon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#6b7280' }} />
                              </div>
                              <span className="truncate text-xs">{img}</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeImage(img, false)}
                              className="h-7 w-7 p-0 flex-shrink-0 hover:bg-red-50"
                            >
                              <X className="h-3.5 w-3.5" style={{ color: '#6b7280' }} />
                            </Button>
                          </div>
                        ))}
                      </div>


                    )}

                    {/* Pending Images (not yet submitted) */}
                    {pendingImages.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs" style={{ color: '#f59e0b' }}>
                            Ready to submit ({pendingImages.length})
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setPendingImages([]);
                              toast.info('All pending images cleared');
                            }}
                            className="h-6 text-xs px-2"
                            style={{ color: '#6b7280' }}
                          >
                            Clear All
                          </Button>
                        </div>
                        {pendingImages.map((img, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2.5 rounded-lg border-2 hover:shadow-sm transition-shadow" style={{ borderColor: '#fbbf24', backgroundColor: '#fffbeb' }}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <div className="p-1.5 rounded" style={{ backgroundColor: '#fef3c7' }}>
                                <ImageIcon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#f59e0b' }} />
                              </div>
                              <span className="truncate text-xs" style={{ color: '#92400e' }}>{img.name}</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeImage(img, true)}
                              className="h-7 w-7 p-0 flex-shrink-0 hover:bg-red-50"
                            >
                              <X className="h-3.5 w-3.5" style={{ color: '#92400e' }} />
                            </Button>
                          </div>
                        ))}

                        {/* Submit Button */}
                        <Button
                          onClick={()=>{
                            handleSubmitImages()
                          }}
                          className="w-full h-9 text-xs shadow-sm mt-2"
                          style={{ backgroundColor: '#1f57d6', color: 'white' }}
                        >
                          <Send className="mr-2 h-4 w-4" />
                          Submit {pendingImages.length} Image{pendingImages.length > 1 ? 's' : ''} for Review
                        </Button>
                      </div>
                    )}
                    {Number(project?.p_escrow) ===0 &&   <Button
                          onClick={()=>{
                            handleSubmitImages()
                          }}
                          className="w-full h-9 text-xs shadow-sm mt-2"
                          style={{ backgroundColor: '#1f57d6', color: 'white' }}
                        >
                          <Send className="mr-2 h-4 w-4" />
                          Submit {currentStage} Stage
                        </Button>}

                  </div>
                </div>
              )}

              {/* Homeowner - View Inspection Images */}
              {( (currentRole === 'homeowner' && !isHomeOwnerApproved )|| (currentRole === 'inspector'&& isHomeOwnerApproved) )
               && milestone?.pm_inspection_details[currentStage]?.inspectionUrl?.length>0&& (
                <div className="bg-white rounded-lg p-4 border" style={{ borderColor: '#e5e7eb' }}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="flex items-center gap-2 text-sm" style={{ color: '#18202a' }}>
                      <div className="p-1.5 rounded" style={{ backgroundColor: '#eff6ff' }}>
                        <ImageIcon className="h-3.5 w-3.5" style={{ color: '#1f57d6' }} />
                      </div>
                      Inspection Images ({milestone?.pm_inspection_details[currentStage]?.inspectionUrl.length})
                    </h4>
                    {/* Stage Information */}



                    {milestone?.pm_inspection_details[currentStage]?.inspectionUrl.length > 0&& (
                      <Badge className="text-xs border-0" style={{ backgroundColor: '#f59e0b', color: 'white' }}>
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Pending Review
                      </Badge>
                    )}
                  </div>

                  {/* Disapproval Reason Display */}


                  <div className="grid grid-cols-2 gap-2.5 mb-3">
                    {milestone?.pm_inspection_details[currentStage]?.inspectionUrl.map((img:any, idx:any) => (
                      <div onClick={()=>{
                        setSelectedImage(img)
                      }} key={idx} className="p-2.5 rounded-lg border hover:shadow-sm transition-shadow" style={{ borderColor: '#e5e7eb', backgroundColor: '#fafafa' }}>
                        <div className="flex items-center gap-1.5 mb-2 justify-center">
                          <img src={img} className='w-1/2 h-auto' />
                        </div>

                      </div>
                    ))}
                  </div>
                  <div
  style={{
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '8px 12px',
  }}
>
  <p
    style={{
      fontSize: '13px',
      color: '#374151',
      margin: 0,
    }}
  >
    You are reviewing <strong>{currentStage?.toUpperCase()}</strong> stage inspection images.
  </p>
</div>
                  {/* Approval Actions - Only show if pending */}
                  {milestone?.pm_inspection_details[currentStage]?.inspectionUrl?.length>0  && (
                    <div className="flex gap-2 pt-3 border-t" style={{ borderColor: '#e5e7eb' }}>
                      <Button
                        onClick={()=>{
                         setShowApprovalDialog(true);
                         }}
                        className="flex-1 h-9 text-xs"
                        style={{ backgroundColor: '#10b981', color: 'white' }}
                      >
                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                        Approve Inspection
                      </Button>
                      <Button
                        onClick={() => setShowDisapprovalDialog(true)}
                        variant="outline"
                        className="flex-1 h-9 text-xs"
                        style={{ borderColor: '#ef4444', color: '#ef4444' }}
                      >
                        <XCircle className="mr-1.5 h-3.5 w-3.5" />
                        Disapprove
                      </Button>
                    </div>
                  )}

                  {/* Re-review option if already reviewed */}
                  {/* {milestone?.pm_inspection_details[currentStage]?.isApproved && (
                    <div className="pt-3 border-t" style={{ borderColor: '#e5e7eb' }}>
                      <Button
                        onClick={() => {
                          onUpdate({
                            ...milestone,
                            inspectionStatus: 'pending',
                            inspectionDisapprovalReason: undefined
                          });
                          toast.info('Inspection status reset to pending');
                        }}
                        variant="outline"
                        size="sm"
                        className="w-full h-8 text-xs"
                      >
                        <History className="mr-1.5 h-3 w-3" />
                        Re-review Inspection
                      </Button>
                    </div>
                  )} */}
                </div>
              )}

              {/* Homeowner Approval */}
              {/* {currentRole === 'homeowner' && milestone.workStage === 'completed' && milestone.status !== 'paid' && (
                <div className="rounded-lg p-4 border-2" style={{ backgroundColor: '#eff6ff', borderColor: '#1f57d6' }}>
                  <div className="flex gap-2.5 mb-3">
                    <div className="p-1.5 rounded" style={{ backgroundColor: 'white' }}>
                      <CheckCircle2 className="h-4 w-4" style={{ color: '#1f57d6' }} />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm mb-1" style={{ color: '#18202a' }}>Approval Required</h4>
                      <p className="text-xs leading-relaxed" style={{ color: '#6b7280' }}>
                        The contractor has marked this milestone as completed. Review the work and inspection images above, then approve to enable payment.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleApproval}
                    className="w-full h-9 text-xs shadow-sm"
                    style={{ backgroundColor: '#1f57d6', color: 'white' }}
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Approve Milestone
                  </Button>
                </div>
              )} */}

              {/* Activity History */}
              <div className="milestone-card__activity bg-white rounded-lg p-4 border" style={{ borderColor: 'var(--app-border)' }}>
                <h4 className="mb-3 flex items-center gap-2 text-sm" style={{ color: 'var(--app-text)' }}>
                  <div className="p-1.5 rounded" style={{ backgroundColor: '#eff6ff' }}>
                    <History className="h-3.5 w-3.5" style={{ color: '#1f57d6' }} />
                  </div>
                  Activity History
                </h4>

                {milestone.activities.length > 0 ? (
                  <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
                    {milestone.activities.slice().reverse().map((activity:any) => {
                      const { Icon, color, bg } = getActivityIcon(activity.action);
                      const images = activity.image ||[]

                      return (
                        <div key={activity.id} className="milestone-card__activity-event flex gap-3 p-3 rounded-lg border hover:shadow-sm transition-shadow" style={{ borderColor: 'var(--app-border)' }}>
                          <div
                            className="mt-0.5 p-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: bg }}
                          >
                            <Icon
                              className="h-3.5 w-3.5"
                              style={{ color: color }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3 mb-1">
                              <p className="text-xs">
                                <span className="font-medium" style={{ color: activity.role === 'contractor' ? '#6b7280' : '#1f57d6' }}>
                                  {activity.user}
                                </span>
                                <span style={{ color: 'var(--app-text)' }}> {activity.action}</span>
                              </p>
                              <span className="text-xs whitespace-nowrap flex-shrink-0" style={{ color: '#9ca3af' }}>
                                {formatTimestamp(activity.timestamp)}
                              </span>
                            </div>
                            {activity.details && (
                              <p className="milestone-card__activity-detail text-xs leading-relaxed p-2 rounded border" style={{ color: 'var(--app-text-muted)', borderColor: 'var(--app-border)' }}>
                                {activity.details}
                              </p>
                            )}
                            {/* Display images if activity has images */}
                            {images.length > 0 && (
                              <div className="mt-2 grid grid-cols-4 gap-2">
                                {images.map((img:any, idx:any) => (
                                  <div
                                    key={idx}
                                    className="relative group  cursor-pointer overflow-hidden rounded-lg border-2 hover:border-blue-400 transition-all"
                                    style={{ borderColor: '#e5e7eb' }}
                                    onClick={() => setEnlargedImage(img)}
                                  >
                                    <div className="aspect-square flex items-center justify-center" style={{  }}>
                                      <img src={img} className="w-full" style={{  }} />
                                    </div>


                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="milestone-card__activity-empty text-center py-6 rounded-lg">
                    <History className="h-8 w-8 mx-auto mb-2" style={{ color: '#d1d5db' }} />
                    <p className="text-xs" style={{ color: '#9ca3af' }}>No activity yet</p>
                  </div>
                )}

                {/* Add Note */}
                {/* {currentRole && (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: '#e5e7eb' }}>
                    <Label className="mb-2 block text-xs" style={{ color: '#6b7280' }}>Add Note</Label>
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Add a note or comment..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        rows={2}
                        className="flex-1 text-xs"
                      />
                      <Button
                        size="sm"
                        onClick={addNote}
                        disabled={!newNote.trim()}
                        className="self-end h-9 text-xs"
                        style={!newNote.trim() ? {} : { backgroundColor: '#1f57d6', color: 'white' }}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                )} */}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Disapproval Dialog */}
      <Dialog open={showDisapprovalDialog} className='tailwind-prefix' >
        <DialogContent className="sm:max-w-[480px]">
            <div className="flex items-center gap-2 text-lg">
              <XCircle className="h-5 w-5" style={{ color: '#ef4444' }} />
              Disapprove Inspection
            </div>
            <div className="text-sm mt-2 text-gray-500">
              Please provide a reason for disapproving the inspection images. This will be shared with the contractor.
            </div>

          <div className="py-4">
            <Label htmlFor="disapproval-reason" className="text-xs mb-2 block" style={{ color: '#6b7280' }}>
              Reason for Disapproval
            </Label>
            <Textarea
              id="disapproval-reason"
              placeholder="e.g., Images are blurry, missing required angles, quality issues..."
              value={disapprovalReason}
              onChange={(e) => setDisapprovalReason(e.target.value)}
              rows={4}
              className="text-sm"
            />
            <p className="text-xs mt-2" style={{ color: '#9ca3af' }}>
              Be specific to help the contractor understand what needs to be corrected.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDisapprovalDialog(false);
                setDisapprovalReason('');
              }}
              className="text-xs h-9"
            >
              Cancel
            </Button>
            <Button
              onClick={handleInspectionDisapproval}
              disabled={!disapprovalReason.trim()}
              className="text-xs h-9"
              style={{ backgroundColor: '#ef4444', color: 'white' }}
            >
              <XCircle className="mr-1.5 h-3.5 w-3.5" />
              Submit Disapproval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectedImage !== null && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute bg-transparent border-0 top-20 right-4 text-white  hover:text-slate-300"
            onClick={() => setSelectedImage(null)}
          >
            <X  className="h-8 w-8 " />
          </button>
          <div className="max-w-5xl max-h-full">
            <img
              src={selectedImage}
              className="max-w-full max-h-[90vh] object-contain"
            />
            <div className="text-white text-center mt-4">
              <h3 className="text-xl">{''}</h3>
            </div>
          </div>
        </div>
      )}
      <Dialog open={showApprovalDialog} className='tailwind-prefix'>
  <DialogContent className="sm:max-w-[480px]">
    <div className="flex items-center gap-2 text-lg">
      <CheckCircle className="h-5 w-5" style={{ color: '#22c55e' }} />
      Approve Inspection
    </div>
    <div className="text-sm mt-2 text-gray-500">
      Please confirm that you’ve reviewed and approved the inspection images. Once approved, the contractor will be notified.
    </div>

    <div className="py-4">
      <Label htmlFor="approval-notes" className="text-xs mb-2 block" style={{ color: '#6b7280' }}>
        Approval Notes (optional)
      </Label>
      <Textarea
        id="approval-notes"
        placeholder="e.g., All images look good, everything is clear and meets requirements..."
        value={approvalNotes}
        onChange={(e) => setApprovalNotes(e.target.value)}
        rows={3}
        className="text-sm"
      />
      <p className="text-xs mt-2" style={{ color: '#9ca3af' }}>
        You can leave a note for the contractor, if necessary.
      </p>
    </div>

    <DialogFooter className="gap-2">
      <Button
        variant="outline"
        onClick={() => {
          setShowApprovalDialog(false);
          setApprovalNotes('');
        }}
        className="text-xs h-9"
      >
        Cancel
      </Button>
      <Button
        onClick={handleInspectionApproval}
        className="text-xs h-9"
        style={{ backgroundColor: '#22c55e', color: 'white' }}
      >
        <CheckCircle className="mr-1.5 h-3.5 w-3.5" />
        Approve Inspection
      </Button>
    </DialogFooter>
  </DialogContent>
</Dialog>
<Dialog open={!!enlargedImage} className='tailwind-prefix'>
        <DialogContent className="sm:max-w-4xl">
          <div>
            <div className="flex items-center gap-2 text-lg">
              <ImageIcon className="h-5 w-5" style={{ color: '#1f57d6' }} />
              Inspection image preview

            </div>

          </div>

          <div className="py-6">
            <div className="w-full rounded-lg border-2 overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
              <div className="w-full aspect-video flex items-center justify-center" style={{ backgroundColor: '#f9fafb' }}>
                <div className="text-center">
                  <img src={enlargedImage} className="text-sm w-full" style={{ color: '#6b7280' }}>
                  </img>

                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setEnlargedImage('')}
              className="text-xs h-9"
              style={{ backgroundColor: '#1f57d6', color: 'white' }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
